import React, { useContext, useEffect, useState } from "react";
import { Loader2, Plus, FileText, MessageCircle, Mail, Smartphone, Phone, X } from "lucide-react";
import { callComConfigApi } from "./callComConfigApi";
import AuthContext  from "../../../store/auth-context";
import ActiveStoreContext from "../../../store/ActiveStoreContext";
import styles from "./ComConfigMyTemplates.module.css";

// Two workarounds worth knowing about: channel options for the "add"
// form are derived from comConfigGetSenderProfiles (which already
// returns channelId/channelCode/channelName per row) rather than a
// dedicated channel-list call, since the store-facing 17 doesn't
// include one. Recipient types aren't fetchable from any store-facing
// endpoint either, so they're hardcoded below — both are small, stable
// reference sets, but flagging the workaround rather than hiding it.

const CHANNEL_ICONS = {
    WHATSAPP: MessageCircle,
    SMS: Smartphone,
    EMAIL: Mail,
    VOICE: Phone,
};

const RECIPIENT_TYPES = [
    { code: "CUSTOMER", name: "Customer" },
    { code: "STAFF", name: "Staff" },
    { code: "OWNER", name: "Salon Owner" },
    { code: "BRANCH_MANAGER", name: "Branch Manager" },
    { code: "ADMIN", name: "Admin" },
    { code: "ACCOUNTANT", name: "Accountant" },
    { code: "RECRUITER", name: "Recruiter" },
];

const emptyForm = {
    eventId: "",
    channelId: "",
    recipientTypeCode: "",
    languageCode: "en",
    content: "",
    externalTemplateRef: "",
    senderProfileId: "",
};

const ComConfigMyTemplates = () => {
    const authCtx = useContext(AuthContext);
    const { activeStore: activeStoreJson } = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return activeStoreJson ? JSON.parse(activeStoreJson) : null; } catch { return null; } })();

    const [events, setEvents] = useState([]);
    const [ownChannels, setOwnChannels] = useState([]); // channels with an ACTIVE own sender profile
    const [templateSlots, setTemplateSlots] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier]);

    const loadAll = async () => {
        if (!activeStore?.storeIdentifier || !authCtx?.token) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);

        try {
            const [eventsRes, profilesRes, slotsRes] = await Promise.all([
                callComConfigApi("comConfigGetEvents", { storeIdentifier: activeStore.storeIdentifier }, authCtx),
                callComConfigApi(
                    "comConfigGetSenderProfiles",
                    { storeIdentifier: activeStore.storeIdentifier },
                    authCtx
                ),
                callComConfigApi(
                    "comConfigGetTemplateSlots",
                    { storeIdentifier: activeStore.storeIdentifier },
                    authCtx
                ),
            ]);

            if (eventsRes?.status === "success") setEvents(Array.isArray(eventsRes.events) ? eventsRes.events : []);

            if (profilesRes?.status === "success") {
                const profiles = Array.isArray(profilesRes.senderProfiles) ? profilesRes.senderProfiles : [];
                const own = profiles.filter(
                    (p) => p.storeIdentifier !== "PLATFORM" && p.status === "ACTIVE"
                );
                setOwnChannels(own);
            }

            if (slotsRes?.status === "success") {
                setTemplateSlots(Array.isArray(slotsRes.templateSlots) ? slotsRes.templateSlots : []);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const handleSubmit = async () => {
        if (!form.eventId || !form.channelId || !form.recipientTypeCode || !form.content) {
            setMessage({ type: "error", text: "Event, channel, recipient and content are required." });
            return;
        }

        setIsSubmitting(true);
        setMessage(null);

        const recipientTypeId = RECIPIENT_TYPES.find((r) => r.code === form.recipientTypeCode)?.id || form.recipientTypeCode;

        const slotRes = await callComConfigApi(
            "comConfigCreateTemplateSlot",
            {
                storeIdentifier: activeStore?.storeIdentifier,
                eventId: form.eventId,
                channelId: form.channelId,
                recipientTypeId,
                languageCode: form.languageCode,
                authIdentifier: authCtx?.authIdentifier,
            },
            authCtx
        );

        const templateId = slotRes?.response?.templateId || slotRes?.templateId;

        if (slotRes?.status !== "success" || !templateId) {
            setMessage({ type: "error", text: slotRes?.message || "Couldn't create the template slot." });
            setIsSubmitting(false);
            return;
        }

        const versionRes = await callComConfigApi(
            "comConfigSaveTemplateVersion",
            {
                templateId,
                content: form.content,
                externalTemplateRef: form.externalTemplateRef,
                senderProfileId: form.senderProfileId,
                authIdentifier: authCtx?.authIdentifier,
            },
            authCtx
        );

        if (versionRes?.status === "success") {
            setMessage({ type: "success", text: "Template saved." });
            setForm(emptyForm);
            setShowAddForm(false);
            loadAll();
        } else {
            setMessage({ type: "error", text: versionRes?.message || "Slot created, but content failed to save." });
        }

        setIsSubmitting(false);
    };

    if (isLoading) {
        return (
            <div className={styles.stateContainer}>
                <Loader2 className={styles.spinner} size={22} />
                <span>Loading your templates…</span>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.title}>My templates</h1>
                    <p className={styles.subtitle}>
                        Record the templates already approved under your own WhatsApp or SMS account.
                    </p>
                </div>
                <button className={styles.addButton} onClick={() => setShowAddForm(true)}>
                    <Plus size={15} />
                    Add template
                </button>
            </div>

            {ownChannels.length === 0 && (
                <div className={styles.noticeBar}>
                    Connect your own WhatsApp, SMS, or email account in Sender connections before adding templates
                    here.
                </div>
            )}

            <div className={styles.slotList}>
                {templateSlots.map((slot) => {
                    const ChannelIcon = CHANNEL_ICONS[slot.channelCode] || MessageCircle;
                    return (
                        <div key={slot.ID} className={styles.slotRow}>
                            <ChannelIcon size={16} />
                            <div className={styles.slotInfo}>
                                <span className={styles.slotEvent}>{slot.eventCode}</span>
                                <span className={styles.slotMeta}>
                                    {slot.recipientTypeCode} · {slot.languageCode}
                                </span>
                            </div>
                            {slot.selectedTemplateVersionId ? (
                                <span className={styles.slotStatusActive}>In use</span>
                            ) : (
                                <span className={styles.slotStatusEmpty}>No content yet</span>
                            )}
                        </div>
                    );
                })}

                {templateSlots.length === 0 && (
                    <div className={styles.emptyState}>
                        <FileText size={20} />
                        <p>No templates of your own yet.</p>
                    </div>
                )}
            </div>

            {showAddForm && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Add a template</h2>
                            <button className={styles.closeButton} onClick={() => setShowAddForm(false)}>
                                <X size={16} />
                            </button>
                        </div>

                        <div className={styles.formGrid}>
                            <label className={styles.fieldLabel}>
                                Event
                                <select
                                    className={styles.fieldSelect}
                                    value={form.eventId}
                                    onChange={(e) => updateForm("eventId", e.target.value)}
                                >
                                    <option value="">Select an event</option>
                                    {events.map((event) => (
                                        <option key={event.ID} value={event.ID}>
                                            {event.eventName}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className={styles.fieldLabel}>
                                Channel
                                <select
                                    className={styles.fieldSelect}
                                    value={form.channelId}
                                    onChange={(e) => {
                                        const channel = ownChannels.find((c) => String(c.channelId) === e.target.value);
                                        updateForm("channelId", e.target.value);
                                        updateForm("senderProfileId", channel?.ID || "");
                                    }}
                                >
                                    <option value="">Select a channel</option>
                                    {ownChannels.map((channel) => (
                                        <option key={channel.channelId} value={channel.channelId}>
                                            {channel.channelName}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className={styles.fieldLabel}>
                                Recipient
                                <select
                                    className={styles.fieldSelect}
                                    value={form.recipientTypeCode}
                                    onChange={(e) => updateForm("recipientTypeCode", e.target.value)}
                                >
                                    <option value="">Select a recipient</option>
                                    {RECIPIENT_TYPES.map((r) => (
                                        <option key={r.code} value={r.code}>
                                            {r.name}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className={styles.fieldLabel}>
                                Language code
                                <input
                                    className={styles.fieldInput}
                                    type="text"
                                    value={form.languageCode}
                                    onChange={(e) => updateForm("languageCode", e.target.value)}
                                />
                            </label>

                            <label className={styles.fieldLabelFull}>
                                Approved content
                                <textarea
                                    className={styles.fieldTextarea}
                                    rows={4}
                                    value={form.content}
                                    onChange={(e) => updateForm("content", e.target.value)}
                                    placeholder="Hi {{customerName}}, your appointment at {{storeName}} is confirmed..."
                                />
                            </label>

                            <label className={styles.fieldLabelFull}>
                                External template reference (the name/ID Meta or DLT approved this under)
                                <input
                                    className={styles.fieldInput}
                                    type="text"
                                    value={form.externalTemplateRef}
                                    onChange={(e) => updateForm("externalTemplateRef", e.target.value)}
                                />
                            </label>
                        </div>

                        {message && message.type === "error" && (
                            <div className={styles.messageError}>{message.text}</div>
                        )}

                        <div className={styles.modalActions}>
                            <button className={styles.cancelButton} onClick={() => setShowAddForm(false)}>
                                Cancel
                            </button>
                            <button className={styles.saveButton} onClick={handleSubmit} disabled={isSubmitting}>
                                {isSubmitting ? <Loader2 size={14} className={styles.spinnerInline} /> : null}
                                Save template
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {message && message.type === "success" && (
                <div className={styles.messageSuccess}>{message.text}</div>
            )}
        </div>
    );
};

export default ComConfigMyTemplates;